<script lang="ts">
  // libs
  import * as _ from "lodash";
  import { authorizationCheck } from "@shared/svelte/Modules/AuthorizationCheck.svelte";
  import * as LocaleString from "@shared/svelte/Components/DateFormatter/LocaleString.svelte";

  // services
  import * as InvoiceService from "@retailer/services/invoice_service";
  import * as PaymentsService from "@retailer/services/payments_service";
  import Flash from "@shared/services/flashv2.js";

  // components
  import PayWithCreditsModal from "@retailer/svelte/Payments/PayWithCreditsModal.svelte";

  // stores
  import { InvoiceStore } from "@retailer/stores/InvoiceStore";
  import { PaymentsStore } from "@retailer/stores/PaymentsStore";
  import { dwollaIdentityStore, userDisabled } from "@retailer/svelte/stores/DwollaIdentityStore";

  // types
  import type { PaymentInstrument } from "@shared/types/PaymentInstrument";
  import type { Invoice, PaymentTransferTypeSmall } from "@shared/types/Invoice";

  // props
  export let invoice: Invoice;

  // data
  let shouldShowPayInvoicesModal = false;
  const currentDateString = new Date().toISOString();

  // functions
  function buildScheduledDate(scheduledTransfer: PaymentTransferTypeSmall) {
    const dateString = scheduledTransfer ? scheduledTransfer.scheduled_for : currentDateString;
    return LocaleString.convertISODateStringToTimeZoneSafeFormattedLocaleDate(dateString);
  }
  function togglePayInvoicesModal() {
    shouldShowPayInvoicesModal = !shouldShowPayInvoicesModal;
  }
  const setScheduledTransferInfo = () => {
    scheduledTransfer = $InvoiceStore.invoice.payment_transfers.find(
      pt => pt.status == "scheduled",
    );
    paymentInstrumentNickname = scheduledTransfer?.payment_method.nickname;
    scheduledDate = buildScheduledDate(scheduledTransfer);
  };
  async function onSchedulePayment(
    selectedPaymentMethod: PaymentInstrument,
    chosenDate: string,
    creditDistributorIds: number[] = [],
    selectedCredits: { [k: string]: number[] } = {},
  ) {
    togglePayInvoicesModal();

    if (typeof selectedPaymentMethod == "string") return;

    await PaymentsService.schedulePayments(
      selectedPaymentMethod.id,
      chosenDate,
      [invoice.id],
      scheduledPaymentSuccessCallback,
      scheduledPaymentErrorCallback,
      creditDistributorIds,
      selectedCredits,
    );

    setScheduledTransferInfo();
  }
  const scheduledPaymentSuccessCallback = async (futureDate: boolean) => {
    const msg = futureDate ? "Your payment has been scheduled." : "Your payment was submitted.";
    Flash.now({ type: "success", message: msg });

    await InvoiceService.get($InvoiceStore.invoice.id);
  };
  const scheduledPaymentErrorCallback = () => {
    let message = $PaymentsStore.errors.map(e => e.error).join(", ");
    Flash.now({ type: "error", message: message });
  };

  // reactives
  $: scheduledTransfer = $InvoiceStore.invoice.payment_transfers.find(
    pt => pt.status == "scheduled",
  );
  $: paymentInstrumentNickname = scheduledTransfer?.payment_method.nickname;
  $: scheduledDate = buildScheduledDate(scheduledTransfer);
  $: buttonDisabled =
    !authorizationCheck("invoices.pay") ||
    parseFloat(invoice.amount_due) <= 0 ||
    !$InvoiceStore.loaded ||
    !$dwollaIdentityStore.loaded ||
    $userDisabled ||
    !invoice.is_payable;
</script>

<button
  class="fizz-button-secondary fizz-full-width fizz-stack-16"
  on:click={togglePayInvoicesModal}
  id="pay-invoice-button"
  data-testid="pay_invoice_btn"
  disabled={buttonDisabled}
>
  Pay Invoice
</button>

<PayWithCreditsModal
  {shouldShowPayInvoicesModal}
  bind:scheduledDate
  invoices={[invoice]}
  {onSchedulePayment}
  {togglePayInvoicesModal}
/>
